import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import { customerLabel } from "./CustomerSelect";
import ImportExportBar from "./ImportExportBar";

const API = "/api/v1/beat-customer-assignments";
const VISIT_DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const beatLabel = (b) => `${b.beatName}${b.beatCode ? ` (${b.beatCode})` : ""}`;

/**
 * Which beat each customer is on (a customer is on one beat at a time). Assign customers to a beat, edit (move to another beat,
 * change the visit day / sequence) or delete an assignment, or import a sheet with Customer Code, Customer Name and Beat Name.
 */
const CustomerBeats = () => {
  const [rows, setRows] = useState([]);
  const [beats, setBeats] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterBeat, setFilterBeat] = useState("");
  const [search, setSearch] = useState("");

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ beatId: "", visitDay: "MON", customerIds: new Set() });
  const [customerSearch, setCustomerSearch] = useState("");
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ beatId: "", visitDay: "MON", visitSequence: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get(API);
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load customer beats", error);
      toast.error(error.message || "Failed to load customer beats");
    } finally {
      setLoading(false);
    }
  };

  const loadLookups = async () => {
    try {
      const [beatData, customerData] = await Promise.all([api.get("/api/v1/beats", { size: 1000 }), api.getAll("/api/v1/customers")]);
      setBeats(beatData?.content || []);
      setCustomers(customerData?.content || []);
    } catch (error) {
      console.error("Failed to load beats / customers", error);
      toast.error(error.message || "Failed to load beats / customers");
    }
  };

  useEffect(() => {
    load();
    loadLookups();
  }, []);

  const refresh = () => {
    load();
    loadLookups();
  };

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (!filterBeat || String(row.beatId) === filterBeat) &&
        (!term || `${row.retailerCode || ""} ${row.customerName} ${row.beatName} ${row.phone || ""}`.toLowerCase().includes(term))
    );
  }, [rows, filterBeat, search]);

  const assignmentOf = useMemo(() => new Map(rows.map((row) => [row.customerId, row])), [rows]);

  const openCreate = () => {
    setCreateForm({ beatId: filterBeat || "", visitDay: "MON", customerIds: new Set() });
    setCustomerSearch("");
    setOnlyUnassigned(true);
    setCreating(true);
  };

  const toggleCustomer = (id) =>
    setCreateForm((prev) => {
      const next = new Set(prev.customerIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, customerIds: next };
    });

  const handleCreate = async () => {
    if (!createForm.beatId) {
      toast.error("Select a beat");
      return;
    }
    if (createForm.customerIds.size === 0) {
      toast.error("Select at least one customer");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/api/v1/beats/${createForm.beatId}/customers/bulk-assign`, {
        customerIds: Array.from(createForm.customerIds),
        visitDay: createForm.visitDay,
      });
      toast.success(`${createForm.customerIds.size} customer(s) assigned`);
      setCreating(false);
      refresh();
    } catch (error) {
      console.error("Failed to assign customers", error);
      toast.error(error.message || "Failed to assign customers");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({ beatId: String(row.beatId), visitDay: row.visitDay || "MON", visitSequence: String(row.visitSequence ?? "") });
  };

  const handleUpdate = async () => {
    if (!editForm.beatId) {
      toast.error("Select a beat");
      return;
    }
    const sequence = editForm.visitSequence.trim();
    if (sequence && (!/^\d+$/.test(sequence) || Number(sequence) < 1)) {
      toast.error("Visit sequence must be a whole number of 1 or more");
      return;
    }
    setSaving(true);
    try {
      await api.put(`${API}/${editing.id}`, {
        beatId: Number(editForm.beatId),
        visitDay: editForm.visitDay,
        visitSequence: sequence ? Number(sequence) : null,
      });
      toast.success("Assignment updated");
      setEditing(null);
      refresh();
    } catch (error) {
      console.error("Failed to update assignment", error);
      toast.error(error.message || "Failed to update assignment");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove ${row.customerName} from beat ${row.beatName}?`)) return;
    try {
      await api.del(`${API}/${row.id}`);
      toast.success("Assignment deleted");
      refresh();
    } catch (error) {
      console.error("Failed to delete assignment", error);
      toast.error(error.message || "Failed to delete assignment");
    }
  };

  const pickableCustomers = customers.filter((c) => {
    if (c.active === false) return false;
    const current = assignmentOf.get(c.id);
    if (onlyUnassigned && current) return false;
    if (current && String(current.beatId) === String(createForm.beatId)) return false;
    const term = customerSearch.trim().toLowerCase();
    return !term || `${customerLabel(c)} ${c.phone || ""}`.toLowerCase().includes(term);
  });

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading customer beats...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <button className="primary" onClick={openCreate}>
          + Assign Customers
        </button>
        <select value={filterBeat} onChange={(e) => setFilterBeat(e.target.value)} aria-label="Beat">
          <option value="">All beats</option>
          {beats.map((b) => (
            <option key={b.id} value={b.id}>
              {beatLabel(b)}
            </option>
          ))}
        </select>
        <input placeholder="Search customer / beat" value={search} onChange={(e) => setSearch(e.target.value)} />
        <ImportExportBar importPath="/api/v1/beat-customer-assignments/import" label="Customer Beats" onImported={refresh} />
      </div>
      <div className="page-subtitle" style={{ margin: "4px 0 10px" }}>
        Excel import columns: <b>Customer Code</b>, <b>Customer Name</b>, <b>Beat Name</b> (optional: Visit Day, Visit Sequence). The
        customer is found by code, else by name; a customer on another beat is moved.
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Customer Code</th>
              <th>Customer</th>
              <th>Phone</th>
              <th>Beat</th>
              <th>Visit Day</th>
              <th>Sequence</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={7}>No customer is assigned to a beat yet.</td>
              </tr>
            ) : (
              shown.map((row) => (
                <tr key={row.id}>
                  <td>{row.retailerCode || "-"}</td>
                  <td>{row.customerName}</td>
                  <td>{row.phone || "-"}</td>
                  <td>{row.beatName}</td>
                  <td>{row.visitDay}</td>
                  <td>{row.visitSequence}</td>
                  <td className="action-buttons flex gap-2">
                    <button className="secondary" onClick={() => openEdit(row)}>
                      Edit
                    </button>
                    <button className="secondary" onClick={() => handleDelete(row)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {creating ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setCreating(false)}>
          <div className="modal-box" style={{ maxWidth: "720px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Assign Customers to Beat</h3>
              <button className="close-btn" onClick={() => setCreating(false)}>
                Close
              </button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Beat</label>
                <select value={createForm.beatId} onChange={(e) => setCreateForm((prev) => ({ ...prev, beatId: e.target.value }))}>
                  <option value="">-- Select beat --</option>
                  {beats.map((b) => (
                    <option key={b.id} value={b.id}>
                      {beatLabel(b)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Visit Day</label>
                <select value={createForm.visitDay} onChange={(e) => setCreateForm((prev) => ({ ...prev, visitDay: e.target.value }))}>
                  {VISIT_DAYS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group" style={{ marginTop: "8px" }}>
              <label>Customers ({createForm.customerIds.size} selected)</label>
              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <input placeholder="Search customers" value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} />
                <label style={{ display: "inline-flex", gap: "6px", alignItems: "center", whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={onlyUnassigned} onChange={(e) => setOnlyUnassigned(e.target.checked)} />
                  Only customers without a beat
                </label>
              </div>
            </div>
            <div style={{ maxHeight: "40vh", overflowY: "auto", border: "1px solid #eee", borderRadius: "8px" }}>
              {pickableCustomers.length === 0 ? (
                <div style={{ padding: "8px 10px" }}>No customers found.</div>
              ) : (
                pickableCustomers.map((customer) => {
                  const current = assignmentOf.get(customer.id);
                  return (
                    <label
                      key={customer.id}
                      style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 10px", borderBottom: "1px solid #f1f1f1" }}
                    >
                      <input type="checkbox" checked={createForm.customerIds.has(customer.id)} onChange={() => toggleCustomer(customer.id)} />
                      <span>
                        {customerLabel(customer)}
                        {current ? <span style={{ color: "#b45309" }}> — now on {current.beatName}, will move</span> : null}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={handleCreate} disabled={saving}>
                {saving ? "Saving..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editing ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
          <div className="modal-box" style={{ maxWidth: "560px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Edit Assignment — {editing.customerName}</h3>
              <button className="close-btn" onClick={() => setEditing(null)}>
                Close
              </button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Beat</label>
                {/* a move to another beat takes the next sequence there unless one is typed */}
                <select value={editForm.beatId} onChange={(e) => setEditForm((prev) => ({ ...prev, beatId: e.target.value, visitSequence: "" }))}>
                  {beats.map((b) => (
                    <option key={b.id} value={b.id}>
                      {beatLabel(b)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Visit Day</label>
                <select value={editForm.visitDay} onChange={(e) => setEditForm((prev) => ({ ...prev, visitDay: e.target.value }))}>
                  {VISIT_DAYS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Visit Sequence</label>
                <input
                  value={editForm.visitSequence}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, visitSequence: e.target.value }))}
                  placeholder="Next on the day"
                  inputMode="numeric"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={handleUpdate} disabled={saving}>
                {saving ? "Saving..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CustomerBeats;
